import { useCallback, useEffect, useState } from "react"

import { getEventDetails } from "../../../Calendar/api/services.ts"
import { type Event } from "../../../Calendar/types/types.ts"

interface UseActivityEventResult {
  event: Event | null
  loading: boolean
  error: boolean
  refetch: () => void
}

/**
 * The lead-embedded event (`lead.lead_calendar_events`) has no address or
 * meeting link, so the popover fetches the full Calendar event instead —
 * only while it is open, never eagerly for a table full of rows.
 */
export function useActivityEvent(
  eventId: string | null | undefined,
  open: boolean
): UseActivityEventResult {
  const [event, setEvent] = useState<Event | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(false)

  const fetchEvent = useCallback(() => {
    if (!eventId) return
    let cancelled = false
    setLoading(true)
    setError(false)

    getEventDetails(eventId)
      .then((response) => {
        if (cancelled) return
        // Unlike the CRM endpoints, the calendar event detail route answers
        // with the plain Event object, not the {status, details} envelope —
        // `calendar.thunks.ts`'s own fetchEventDetails reads it the same way,
        // despite the service layer's (stale) BaseApiResponse<Event> typing.
        const data = response.data as unknown as Event | undefined
        if (response.ok && data?.id) {
          setEvent(data)
        } else {
          setError(true)
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [eventId])

  useEffect(() => {
    if (!open) {
      setEvent(null)
      setError(false)
      return
    }
    return fetchEvent()
  }, [open, fetchEvent])

  return { event, loading, error, refetch: fetchEvent }
}
